<template>
  <div class="list-controls">
    <!-- Transaction Type Selector -->
    <div class="transaction-type-selector">
      <SingleSelect
        :options="statuses"
        :value="sTransactionStatus"
        :placeholder="placeholder"
        :aria-label="tConversation('page.issue.status')"
        @input="handleTransactionTypeChange"
      />
    </div>

    <!-- Pagination Controls -->
    <div class="pagination-controls">
      <!-- Previous Button -->
      <button
        :disabled="isPrevDisabled"
        class="pagination-button prev-button"
        :class="{ disabled: isPrevDisabled }"
        @click="handlePrevPage"
      >
        {{ tConversation('page.issue.transaction.prev') }}
      </button>

      <!-- Page Selector -->
      <div class="page-selector">
        <label for="page-selector">{{ tConversation('page.issue.transaction.page') }}</label>
        <SingleSelect
          :options="pageOptions"
          :value="sCurrentPage"
          :placeholder="String(sCurrentPage)"
          :aria-label="tConversation('page.issue.transaction.page')"
          @input="handlePageChange"
        />
        <span class="page-info">/ {{ totalPages }}</span>
      </div>

      <!-- Next Button -->
      <button
        :disabled="isNextDisabled"
        class="next-button pagination-button"
        :class="{ disabled: isNextDisabled }"
        @click="handleNextPage"
      >
        {{ tConversation('page.issue.transaction.next') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, PropSync, Vue } from 'vue-property-decorator';
import { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import SingleSelect from '@/components/SingleSelect.vue';

interface TransactionStatusOption {
  value: ExternalTransactionKind;
  label: string;
}

@Component({
  components: {
    SingleSelect,
  },
})
export default class ListControls extends Vue {
  @PropSync('currentPage', { type: Number, required: true }) sCurrentPage!: number;
  @PropSync('transactionStatus', { required: true }) sTransactionStatus!: ExternalTransactionKind;
  @Prop(String) readonly placeholder!: string;
  @Prop({ required: true }) readonly totalPages!: number;
  @Prop({ required: true }) readonly transactionStatuses!: TransactionStatusOption[];

  get isPrevDisabled() {
    return this.sCurrentPage === 1;
  }

  get isNextDisabled() {
    return this.sCurrentPage === this.totalPages;
  }

  get statuses() {
    const emptyOption = {
      value: '',
      label: this.tConversation('page.issue.status'),
    };
    return [emptyOption, ...this.transactionStatuses];
  }

  get pageOptions() {
    return Array.from({ length: this.totalPages }, (_, index) => ({
      value: index + 1,
      label: String(index + 1),
    }));
  }

  handleTransactionTypeChange(value: string | number) {
    this.sTransactionStatus = value as ExternalTransactionKind;
  }

  handlePageChange(value: string | number) {
    this.sCurrentPage = Number(value);
  }

  handlePrevPage() {
    if (this.isPrevDisabled) return;
    this.sCurrentPage = this.sCurrentPage - 1;
  }

  handleNextPage() {
    if (this.isNextDisabled) return;
    this.sCurrentPage = this.sCurrentPage + 1;
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';

.list-controls {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 1rem;
  background-color: $tr-controls-background;
  border-radius: 8px;
  margin: 1rem 0;
  color: $tr-controls-text-color;
  font-size: 1.2rem;
  min-width: 0;

  .transaction-type-selector {
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
    max-width: 85px;
  }

  .pagination-controls {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    min-width: 0;

    .pagination-button {
      color: $tr-controls-text-color;
      padding: 0.5rem 0.75rem;
      border: 1px solid $tr-controls-selector-border-color;
      border-radius: 4px;
      background-color: $tr-controls-selector-background;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .page-selector {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .custom-selector {
        max-width: 40px;
      }

      label {
        font-weight: 600;
        color: $tr-controls-text-color;
      }

      .page-info {
        font-weight: 600;
        color: $tr-controls-text-color;
        white-space: nowrap;
      }
    }
  }
}
</style>
